import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { withTranslation } from "react-i18next";
import ViewIcon from "@mui/icons-material/RemoveRedEye";
import { Button, Grid, IconButton } from "@mui/material";
import moment from "moment";
import PropTypes from "prop-types";

import BaseTable from "Components/Common/BaseTable";
import BreadCrumb from "Components/Common/BreadCrumb";
import InputFieldText from "Components/Common/InputFieldText";
import SelectField from "Components/Common/SelectField";
import DocumentPreviewModal from "Components/Common/DocumentPreviewModal";
import { billingSubscriptionTypes, getOptionLabel } from "common/enums";
import Tooltip from "Components/Common/Tooltip";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";
import { getImageUrl, isAuthorizedUser } from "utils";

const RECORDS_PER_PAGE = 10;

const UserBills = ({ t }) => {
  const resetPageRef = useRef();
  const { roles } = useSelector((state) => state.Login);

  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);
  const [documentToPreview, setDocumentToPreview] = useState(null);
  const [subscriptionType, setSubscriptionType] = useState("");
  const [consumerNumber, setConsumerNumber] = useState("");
  const [query, setQuery] = useState({});

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const serverSidePaginationConfig = useMemo(
    () => ({
      endpoint: "/bill-calculation/user",
      query,
    }),
    [query]
  );

  const updatedBillingSubscriptionOptions = useMemo(
    () =>
      billingSubscriptionTypes.filter(({ allowedRoles }) =>
        isAuthorizedUser(roles, allowedRoles)
      ),
    [billingSubscriptionTypes]
  );

  const columns = useMemo(
    () => [
      { title: "Bill Cycle", field: "billCycle" },
      { title: "Consumer Number", field: "consumerNumber" },
      { title: "Consumer Name", field: "consumerName" },
      { title: "Net Payable", field: "netPayable" },
      { title: "Amount Paid", field: "amountPaid" },
      { title: "Pending Dues", field: "pendingDues" },
      { title: "Current Month Bill", field: "currentMonthBill" },
      {
        title: "Status",
        renderCell: ({ status }) =>
          getOptionLabel("billCalculationStatus", status),
      },
      {
        title: "Consumer Status",
        renderCell: ({ utilityConsumerStatus }) =>
          getOptionLabel("utilityConsumerStatusEnum", utilityConsumerStatus),
      },
      {
        title: "Payment Due Date",
        renderCell: ({ paymentDueDate }) =>
          moment(paymentDueDate).isValid()
            ? convertUTCDateToLocalDate(paymentDueDate).format("DD-MM-YYYY")
            : "N/A",
      },
      {
        title: "Actions",
        renderCell: ({ url }) => (
          <IconButton
            onClick={() =>
              setDocumentToPreview({
                url: getImageUrl(url),
                documentTypeName: "Bill",
                documentType: "pdf",
                documentName: "bill",
              })
            }
          >
            <Tooltip title={t("View Bill Receipt")}>
              <ViewIcon
                fontSize="small"
                color="primary"
                className="cursor-pointer"
              />
            </Tooltip>
          </IconButton>
        ),
      },
    ],
    []
  );

  useEffect(() => {
    if (updatedBillingSubscriptionOptions.length) {
      setSubscriptionType(updatedBillingSubscriptionOptions[0].value);
    }
  }, [updatedBillingSubscriptionOptions]);

  return (
    <>
      {!!documentToPreview && (
        <DocumentPreviewModal
          isOpen
          handleClose={() => setDocumentToPreview(null)}
          {...documentToPreview}
        />
      )}

      <BreadCrumb title={t("User Bills")} pageTitle={t("Billing")} />

      <Grid container rowSpacing={1} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
        <Grid item xs={12} sm={6} md={4}>
          <SelectField
            label={t("Subscription Type") + "*"}
            onChange={(_, { value }) => {
              setSubscriptionType(value);
            }}
            value={
              updatedBillingSubscriptionOptions.find(
                ({ value }) => value === subscriptionType
              ) ?? ""
            }
            options={updatedBillingSubscriptionOptions}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <InputFieldText
            label={t("Consumer Number")}
            value={consumerNumber}
            type="number"
            onChange={(e) => setConsumerNumber(e.target.value)}
            placeholder={t("Enter consumer number")}
          />
        </Grid>

        <Grid item lg={4}>
          <Button
            variant="outlined"
            className="mb-2 me-2"
            onClick={() => {
              setQuery({ consumerNumber, subscriptionType });
              handleResetPage();
            }}
          >
            {t("Search")}
          </Button>
          <Button
            variant="outlined"
            onClick={() => {
              setQuery({ subscriptionType });
              setConsumerNumber("");
              handleResetPage();
            }}
            className="mb-2 me-2"
          >
            {t("Clear")}
          </Button>
        </Grid>
      </Grid>

      <BaseTable
        columns={columns}
        pageSize={RECORDS_PER_PAGE}
        paginationType="serverSide"
        serverSidePaginationConfig={serverSidePaginationConfig}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        maxHeight="calc(100vh - 185px)"
      />
    </>
  );
};

UserBills.propTypes = {
  t: PropTypes.func,
};

export default withTranslation()(UserBills);
